import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 911爆料网';
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string ?? '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d96ahldtfb6ioa7s1m40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(text); } catch { /* ignored */ }

      if (res.ok && parsed && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        const msg = (parsed as { meta?: { message?: string; detail?: string }; message?: string })?.meta?.message
          ?? (parsed as { meta?: { message?: string; detail?: string }; message?: string })?.meta?.detail
          ?? (parsed as { message?: string })?.message
          ?? '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Breadcrumb */}
        <nav aria-label="面包屑导航" className="w-full px-4 md:px-6 lg:px-10 pt-6 pb-2">
          <ol className="flex items-center gap-2 text-xs text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line text-xs"></i>
            </li>
            <li>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </li>
          </ol>
        </nav>

        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[500px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20professional%20newsroom%20workspace%20with%20warm%20ambient%20lighting%20elegant%20dark%20green%20and%20gold%20color%20scheme%20sophisticated%20editorial%20atmosphere%20abstract%20composition%20with%20soft%20shadows%20harmonious%20professional%20mood&width=1800&height=900&seq=about-hero-bg&orientation=landscape"
            alt="关于911爆料网"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white mb-4">关于911爆料网</h1>
            <p className="text-white/70 text-sm md:text-base max-w-xl text-center leading-relaxed">
              聚合全网网红黑料、明星八卦、社会热点事件，做最专业的吃瓜群众专属平台
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-6">品牌故事</h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    911爆料网成立于2023年，是国内领先的娱乐资讯爆料平台。我们致力于为吃瓜群众提供最新、最快、最全的网红黑料、明星八卦和社会热点事件报道。
                  </p>
                  <p>
                    在这个信息爆炸的时代，真假消息鱼龙混杂。911爆料网坚持"有料、有据、有态度"的原则，每一篇爆料都经过严格的多方核实，确保信息的真实性和准确性。
                  </p>
                  <p>
                    我们的团队由资深娱乐记者、自媒体人和网络舆情分析师组成，拥有多年行业经验和广泛的人脉资源，能够第一时间获取圈内一手资讯。
                  </p>
                  <p>
                    我们不只是一家爆料平台，更是吃瓜文化的推动者和引领者。让每一个吃瓜群众都能享受高质量、有深度的娱乐资讯，是我们的使命。
                  </p>
                </div>
              </div>
              <div className="relative aspect-[4/5] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20modern%20editorial%20office%20interior%20with%20warm%20lighting%20shelves%20of%20media%20archives%20sophisticated%20green%20and%20gold%20accents%20professional%20journalism%20atmosphere%20abstract%20artistic%20composition%20soft%20shadows%20harmonious%20mood&width=800&height=1000&seq=about-brand-story&orientation=portrait"
                  alt="911爆料网品牌故事"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Platform Stats */}
        <section className="w-full bg-background-100 py-16 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">平台数据</h2>
              <p className="text-sm text-foreground-500">用数据说话，见证我们的成长</p>
            </div>

            <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {[
                { value: '500万+', label: '月活跃用户', icon: 'ri-user-line' },
                { value: '12,000+', label: '累计爆料文章', icon: 'ri-article-line' },
                { value: '98.5%', label: '信息准确率', icon: 'ri-shield-check-line' },
                { value: '30+', label: '合作媒体机构', icon: 'ri-building-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                    <i className={`${stat.icon} text-xl text-accent-600`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading text-foreground-950 mb-1">{stat.value}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-20">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">核心价值观</h2>
              <p className="text-sm text-foreground-500">三大准则驱动我们前行</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  icon: 'ri-scales-3-line',
                  title: '真实客观',
                  desc: '每一条爆料都经过多方核实，杜绝虚假信息和标题党，对读者负责，对真相负责。',
                },
                {
                  icon: 'ri-flashlight-line',
                  title: '快速及时',
                  desc: '7×24小时全天候追踪娱乐圈动态，第一时间为读者带来最新、最热的爆料资讯。',
                },
                {
                  icon: 'ri-heart-pulse-line',
                  title: '深度专业',
                  desc: '不仅报道表面现象，更深入挖掘事件背后的逻辑与联系，提供有价值的深度分析。',
                },
              ].map((item) => (
                <div key={item.title} className="bg-background-50 rounded-lg p-6 md:p-8 text-center">
                  <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className={`${item.icon} text-xl text-secondary-700`}></i>
                  </div>
                  <h3 className="text-lg font-heading text-foreground-900 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full bg-background-100 py-16 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">常见问题</h2>
                <p className="text-sm text-foreground-500">关于911爆料网的常见疑问解答</p>
              </div>

              <div className="space-y-4">
                {[
                  {
                    q: '911爆料网的内容来源是什么？',
                    a: '我们的内容来源于多方渠道，包括圈内人士爆料、公开报道整理、官方发布信息以及合作媒体供稿。所有内容均经过编辑团队审核确认后发布。',
                  },
                  {
                    q: '如何向911爆料网提供爆料线索？',
                    a: '你可以通过下方的商务合作表单提交爆料线索，或发送邮件至 tips@911baoliao.com。我们会严格保护爆料人的隐私信息。',
                  },
                  {
                    q: '911爆料网的内容是否免费阅读？',
                    a: '目前911爆料网所有内容均免费向公众开放。我们通过广告收入和商务合作维持平台运营，未来不排除推出增值会员服务。',
                  },
                  {
                    q: '发现侵权内容如何处理？',
                    a: '如果你认为平台上的某篇内容侵犯了你的合法权益，可以通过邮件 contact@911baoliao.com 联系我们。我们会在24小时内核实处理。',
                  },
                ].map((faq, idx) => (
                  <details
                    key={idx}
                    className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer"
                  >
                    <summary className="px-5 md:px-6 py-4 md:py-5 text-sm md:text-base font-medium text-foreground-800 flex items-center justify-between gap-4 list-none">
                      {faq.q}
                      <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center transition-transform duration-300 group-open:rotate-180">
                        <i className="ri-arrow-down-s-line text-foreground-400"></i>
                      </div>
                    </summary>
                    <div className="px-5 md:px-6 pb-4 md:pb-5 text-sm text-foreground-500 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-20">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">商务合作</h2>
              <p className="text-sm text-foreground-500">
                广告投放、内容合作、爆料投稿，欢迎联系我们
              </p>
            </div>

            <form
              data-readdy-form=""
              onSubmit={handleSubmit}
              className="bg-background-100 rounded-lg p-6 md:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    placeholder="请输入你的姓名"
                    className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    placeholder="请输入你的邮箱"
                    className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-2">
                  公司/机构
                </label>
                <input
                  id="contact-company"
                  type="text"
                  name="company"
                  placeholder="请输入公司或机构名称"
                  className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="contact-type" className="block text-sm font-medium text-foreground-700 mb-2">
                  合作类型 <span className="text-accent-500">*</span>
                </label>
                <select
                  id="contact-type"
                  name="cooperation_type"
                  required
                  className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer"
                >
                  <option value="">请选择合作类型</option>
                  <option value="广告投放">广告投放</option>
                  <option value="内容合作">内容合作</option>
                  <option value="爆料投稿">爆料投稿</option>
                  <option value="媒体合作">媒体合作</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                  留言内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请输入合作需求或留言内容（500字以内）"
                  className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                ></textarea>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="website-alt-field"
              />

              {formStatus === 'success' && (
                <div className="p-4 rounded-lg bg-primary-50 border border-primary-200/70">
                  <p className="text-sm text-primary-700 flex items-center gap-2">
                    <i className="ri-check-line text-lg"></i>
                    提交成功！我们会尽快与你联系。
                  </p>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="p-4 rounded-lg bg-accent-50 border border-accent-200/70">
                  <p className="text-sm text-accent-700 flex items-center gap-2">
                    <i className="ri-error-warning-line text-lg"></i>
                    {formError}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </span>
                ) : (
                  '提交合作需求'
                )}
              </button>
            </form>
          </div>
        </section>

        {/* SEO Schema.org */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '关于我们 - 911爆料网',
            description: '了解911爆料网的品牌故事、平台数据与核心价值观。聚合网红黑料、明星八卦、社会热点事件的专业吃瓜平台。',
            url: 'https://www.911baoliao.com/about',
            inLanguage: 'zh-CN',
            isPartOf: {
              '@type': 'WebSite',
              name: '911爆料网',
              url: 'https://www.911baoliao.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.911baoliao.com' },
                { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.911baoliao.com/about' },
              ],
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '911爆料网的内容来源是什么？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '我们的内容来源于多方渠道，包括圈内人士爆料、公开报道整理、官方发布信息以及合作媒体供稿。所有内容均经过编辑团队审核确认后发布。',
                },
              },
              {
                '@type': 'Question',
                name: '如何向911爆料网提供爆料线索？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '你可以通过商务合作表单提交爆料线索，或发送邮件至 tips@911baoliao.com。我们会严格保护爆料人的隐私信息。',
                },
              },
              {
                '@type': 'Question',
                name: '911爆料网的内容是否免费阅读？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '目前911爆料网所有内容均免费向公众开放。我们通过广告收入和商务合作维持平台运营，未来不排除推出增值会员服务。',
                },
              },
              {
                '@type': 'Question',
                name: '发现侵权内容如何处理？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '如果你认为平台上的某篇内容侵犯了你的合法权益，可以通过邮件联系。我们会在24小时内核实处理。',
                },
              },
            ],
          })}
        </script>
      </main>
      <Footer />
    </>
  );
}